import { addYears } from '@if/core/date'
import {
  EApplicantType,
  getEGenderList,
  getELanguageList,
  getEMaritalStatusList,
  getERelationToApplicantList,
} from '@if/core/types'
import {
  ControlledCheckbox,
  ControlledPhoneField,
  ControlledSelect,
  ControlledSinField,
  ControlledStringDateOnlyPicker,
  ControlledTextField,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Box, Divider, Grid, Typography } from '@mui/material'
import { Constants } from '@src/types/Constants'
import { ApplicantDto, CreditApplication } from '@src/types/CreditApplicationSchema'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  applicantType: EApplicantType
  formControl: Control<CreditApplication | ApplicantDto>
  hasLegalName: boolean
  hasPrequalifyBeenPressed: boolean
  disableEdit: boolean
  isSinNeeded: boolean | null
}

const ApplicantInformationParameters = ({
  applicantType,
  formControl,
  hasLegalName,
  disableEdit,
  isSinNeeded,
}: Props) => {
  const { t } = useTranslation()

  const genderList = useTranslatedOptionEnum('EGender', getEGenderList(), 'enum')
  const maritalStatusList = useTranslatedOptionEnum('EMaritalStatus', getEMaritalStatusList(), 'enum')
  const languageList = useTranslatedOptionEnum('ELanguage', getELanguageList(), 'enum')
  const relationToApplicantList = useTranslatedOptionEnum('ERelationToApplicant', getERelationToApplicantList(), 'enum')

  const isCoApplicant = applicantType !== EApplicantType.Applicant

  return (
    <Box>
      <Divider style={{ fontSize: 25 }}>
        <Typography variant="h5" gutterBottom>
          {t('editCreditApplication.information.label')}
        </Typography>
      </Divider>
      <Grid container spacing={1} rowSpacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 4,
            md: 3,
          }}
        >
          <ControlledTextField
            id="applicantFirstName"
            name={`${applicantType}.firstName`}
            control={formControl}
            label={t('editCreditApplication.information.firstName')}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
            md: 3,
          }}
        >
          <ControlledTextField
            id="applicantMiddleName"
            name={`${applicantType}.middleName`}
            control={formControl}
            label={`${t('editCreditApplication.information.middleName')} (${String(t('common.optional'))})`}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
            md: 3,
          }}
        >
          <ControlledTextField
            id="applicantLastName"
            name={`${applicantType}.lastName`}
            control={formControl}
            label={t('editCreditApplication.information.lastName')}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            fullWidth
            disabled={disableEdit || hasLegalName}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 8, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
          <ControlledCheckbox
            name={`${applicantType}.hasLegalName`}
            control={formControl}
            label={t('editCreditApplication.information.hasLegalName')}
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
            md: 3,
          }}
        >
          <ControlledStringDateOnlyPicker
            name={`${applicantType}.birthDate`}
            control={formControl}
            label={t('editCreditApplication.information.birthDate')}
            minDate={addYears(new Date(), -Constants.MaxYearsToDisplay)}
            maxDate={new Date()}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 8,
            md: 3,
          }}
        >
          <ControlledSinField
            name={`${applicantType}.sin`}
            control={formControl}
            label={`${t('editCreditApplication.information.sin')} (${String(t('common.optional'))})`}
            fullWidth
            disabled={disableEdit && !isSinNeeded}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <ControlledSelect
            name={`${applicantType}.genderId`}
            control={formControl}
            options={genderList}
            label={t('editCreditApplication.information.genderId')}
            disableEmptyOption
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <ControlledSelect
            name={`${applicantType}.maritalStatusId`}
            control={formControl}
            options={maritalStatusList}
            label={t('editCreditApplication.information.maritalStatusId')}
            disableEmptyOption
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <ControlledSelect
            name={`${applicantType}.languageId`}
            control={formControl}
            options={languageList}
            label={t('editCreditApplication.information.languageId')}
            disableEmptyOption
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        {isCoApplicant && (
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <ControlledSelect
              name={`${applicantType}.relationWithApplicant`}
              control={formControl}
              options={relationToApplicantList}
              label={t('editCreditApplication.information.relationWithApplicant')}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1} rowSpacing={2} sx={{ pt: 2 }}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledPhoneField
            id="cellPhone"
            name={`${applicantType}.cellPhone`}
            control={formControl}
            label={t('editCreditApplication.information.cellPhone')}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledPhoneField
            id="cellPhone"
            name={`${applicantType}.homePhone`}
            control={formControl}
            label={`${String(t('editCreditApplication.information.homePhone'))} (${String(t('common.optional'))})`}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid size={12}>
          <ControlledTextField
            id="email"
            type="email"
            name={`${applicantType}.email`}
            control={formControl}
            label={t('editCreditApplication.information.email')}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 60 } }}
            disabled={disableEdit}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
export default ApplicantInformationParameters
